import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/AppIcon";
import BrandPanel from "../components/BrandPanel";
import { useAuth } from "../context/AuthContext";
import {
  resolveInstitutionByEmail,
  getPublicBranding,
  logoSrc,
} from "../api/client";
import {
  getCurrentSubdomain,
  buildTenantLoginUrl,
  buildTenantUrl,
  isProdRootDomain,
  ROOT_DOMAIN,
} from "../utils/host";

// Institution login is a two-host flow:
//
//   • Apex hub (prepxai.in/institution/login): we ask ONLY for the email, resolve
//     it to the admin's institution, then send the browser to that tenant's own
//     host (<slug>.prepxai.in) to finish with the password. This keeps each
//     institution's session bound to its own branded address.
//   • Tenant host (<slug>.prepxai.in/institution/login): the slug is read from the
//     hostname, branding is loaded, and we show the password step directly (email
//     prefilled from the ?e= param carried over from the hub).
//
// On localhost / preview deploys there are no real subdomains, so after resolving
// we simply reveal the password step in place on the same origin (single-port dev).
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || "/institution";

  // The tenant slug implied by the current host ("" on the apex/localhost).
  const currentSub = useMemo(() => getCurrentSubdomain(), []);
  const prefillEmail = useMemo(
    () => new URLSearchParams(location.search).get("e") || "",
    [location.search]
  );

  // step: 'email' (hub email-first) or 'password' (tenant host / dev fallback).
  const [step, setStep] = useState(currentSub ? "password" : "email");
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [boundSubdomain, setBoundSubdomain] = useState(currentSub || "");
  const [branding, setBranding] = useState(null); // { name, logoUrl(absolute) }
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // On a tenant host, load its public branding so the login screen shows the
  // institution's own name + logo before anyone signs in.
  useEffect(() => {
    let alive = true;
    if (!currentSub) return undefined;
    (async () => {
      try {
        const data = await getPublicBranding(currentSub);
        if (alive && data) {
          setBranding({ name: data.name, logoUrl: logoSrc(data.logoUrl) });
        }
      } catch {
        /* branding is best-effort; the form still works without it */
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentSub]);

  const handleResolve = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    try {
      const res = await resolveInstitutionByEmail(trimmed);
      if (!res?.found || !res.subdomain) {
        setError(
          "We couldn't find an institution for that email. Please check the address, or ask your placement team for your institution's sign-in link."
        );
        return;
      }
      // In production, hand the user off to their own tenant host to finish.
      const tenantUrl = buildTenantLoginUrl(res.subdomain, trimmed);
      if (tenantUrl) {
        window.location.assign(tenantUrl);
        return;
      }
      // Dev / single-origin: reveal the password step in place, bound to the
      // resolved subdomain so the backend's tenant check still passes.
      setBoundSubdomain(res.subdomain);
      setBranding({ name: res.name, logoUrl: logoSrc(res.logoUrl) });
      setStep("password");
    } catch (err) {
      setError(err?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({
        email: email.trim(),
        password,
        subdomain: boundSubdomain || currentSub || "",
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // Wrong tenant host: bounce to the correct one (prod) or bind in place (dev).
      if (err?.correctSubdomain) {
        const url = buildTenantLoginUrl(err.correctSubdomain, email.trim());
        if (url) {
          window.location.assign(url);
          return;
        }
        setBoundSubdomain(err.correctSubdomain);
      }
      setError(err?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // "Use a different email": on a tenant host this means going back to the apex
  // hub; on the hub/dev it just returns to the email step.
  const hubLoginUrl = isProdRootDomain()
    ? `${window.location.protocol}//${ROOT_DOMAIN}/institution/login`
    : "";
  const resetToEmail = () => {
    if (currentSub && hubLoginUrl) {
      window.location.assign(hubLoginUrl);
      return;
    }
    setStep("email");
    setPassword("");
    setError("");
  };

  const isPasswordStep = step === "password";
  const tenantName = branding?.name || "";
  const tenantLogo = branding?.logoUrl || "";

  return (
    <div className="flex min-h-screen bg-background">
      <BrandPanel logoUrl={tenantLogo} institutionName={tenantName} />
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            {tenantLogo ? (
              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-muted p-1">
                <img
                  src={tenantLogo}
                  alt={tenantName ? `${tenantName} logo` : "Institution logo"}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Icon name="GraduationCap" size={20} />
              </div>
            )}
            <div className="leading-tight">
              <div className="text-[15px] font-semibold text-foreground">
                {tenantName || "prepxai"}
              </div>
              <div className="-mt-0.5 text-[11px] text-muted-foreground">
                {tenantName ? "on prepxai" : "for institutions"}
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground">
            {isPasswordStep ? "Welcome back" : "Sign in to your console"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isPasswordStep
              ? tenantName
                ? `Enter your password to access ${tenantName}.`
                : "Enter your password to continue."
              : "Enter your work email and we'll take you to your institution's sign-in page."}
          </p>

          {error && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {isPasswordStep ? (
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <Input
                label="Work email"
                type="email"
                name="email"
                placeholder="you@college.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                readOnly={Boolean(currentSub) && Boolean(prefillEmail)}
              />
              <Input
                label="Password"
                type="password"
                name="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                autoComplete="current-password"
              />
              <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading}>
                Sign in
              </Button>
              <button
                type="button"
                onClick={resetToEmail}
                className="mx-auto block text-xs text-muted-foreground hover:text-foreground hover:underline"
              >
                ← Use a different email
              </button>
            </form>
          ) : (
            <form onSubmit={handleResolve} className="mt-6 space-y-4">
              <Input
                label="Work email"
                type="email"
                name="email"
                placeholder="you@college.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
              />
              <Button type="submit" fullWidth size="lg" loading={loading} disabled={loading} iconName="ArrowRight" iconPosition="right">
                Continue
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don&apos;t have an institution account?{" "}
            <Link to="/institution/signup" className="font-medium text-primary hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
